import { STORAGE_KEY } from "@constants";
import { getMemberSettings } from "@utils/member-settings";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { FormatNumber, PersistLanguage } from "./plugins";

// Each locale file becomes a separate lazy chunk; only the user's language is fetched at runtime.
// This removes ~548 KB of JSON data from the main bundle.
const localeModules = import.meta.glob<{ default: Record<string, unknown> }>("./locales/*.json");

const SUPPORTED_LANGS = ["ar", "bn", "en", "es", "hi", "id", "pt", "th", "tl", "ur"];

const memberSettings = getMemberSettings();
const rawLng =
  localStorage.getItem(STORAGE_KEY.LANGUAGE) ?? (memberSettings?.isLoaded ? memberSettings.language : undefined) ?? "en";
const lng = SUPPORTED_LANGS.includes(rawLng) ? rawLng : "en";

i18next
  .use(FormatNumber)
  .use(PersistLanguage)
  .use(initReactI18next)
  .init({
    lng,
    fallbackLng: "en",
    debug: false,
    interpolation: {
      alwaysFormat: true,
    },
    react: {
      useSuspense: false,
    },
  });

/**
 * Loads the translation bundle for a language if not already loaded.
 * Returns a promise that resolves when the bundle is ready (so callers can await before changing language).
 */
const addLocale = (lang: string): Promise<void> => {
  const loadFn = localeModules[`./locales/${lang}.json`];
  if (!loadFn) return Promise.resolve();
  if (i18next.hasResourceBundle(lang, "translation")) return Promise.resolve();
  return loadFn().then((mod) => {
    if (!i18next.hasResourceBundle(lang, "translation")) {
      i18next.addResourceBundle(lang, "translation", mod.default, true, false);
    }
  });
};

// Wrap changeLanguage so the target locale is loaded before switching.
// Without this, switching to a language that wasn't preloaded (e.g. from LanguagePage) would show empty translations.
const originalChangeLanguage = i18next.changeLanguage.bind(i18next);
i18next.changeLanguage = (language: string) => {
  return addLocale(language).then(() => originalChangeLanguage(language));
};

// Kick off load for current language immediately at module-evaluation time so
// that by the time React finishes bootstrapping (≥500ms away), the locale is ready.
addLocale(lng);
// Pre-fetch English fallback so it is available for any missing keys.
if (lng !== "en") addLocale("en");

/**
 * Schedules loading all locale bundles during idle time. Call this when a component
 * that allows language switch is opened (e.g. LanguagePage, LanguageSelect) so
 * switching language later is instant. Uses requestIdleCallback to avoid blocking.
 */
export const preloadAllLocales = () => {
  const loadAll = () => {
    SUPPORTED_LANGS.forEach((lang) => addLocale(lang));
  };
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(loadAll, { timeout: 2000 });
  } else {
    setTimeout(loadAll, 1);
  }
};

export default i18next;
